import React, { Fragment, useCallback } from 'react';
import { CardCollection, Button, Spinner } from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import Cookies from 'js-cookie';

import { trackGAEvent } from '../../../../utils/gaTracking';

import FeedItem from './FeedItem';
import FeedItemListSkeleton from './FeedItemListSkeleton';

import { TFeedItem } from 'types/TFeeds';

import Classes from './FeedItemList.scss';

type TFeedsItemsProps = {
  items: TFeedItem[];
  isLoading: boolean;
  hasNextPage: boolean;
  onLoadMore: Function;
  feedType: string;
  shouldOpenInNewTab?: boolean;
  isScrollingDisabled?: boolean;
};

const FeedItemList = ({
  items,
  hasNextPage,
  isLoading,
  onLoadMore,
  feedType,
  shouldOpenInNewTab,
  isScrollingDisabled,
}: TFeedsItemsProps) => {
  const { t } = useI18nContext();
  const infiniteRef = useInfiniteScroll<HTMLDivElement>({
    loading: isLoading,
    hasNextPage: !isScrollingDisabled && hasNextPage,
    onLoadMore,
  });

  const handleItemClick = useCallback(
    (correlationId) => {
      Cookies.set('searchRequestId', correlationId);
      trackGAEvent('Clicked', feedType);
    },
    [feedType],
  );

  if (!items.length && !isLoading) {
    return <Trans className={Classes.message} i18nKey="l1.feeds.empty" />;
  }

  return (
    <div ref={infiniteRef}>
      {Boolean(items.length) && (
        <CardCollection kind="grid">
          {items.map((item, i) => (
            <Fragment key={`${item.itemId}-${i}`}>
              <FeedItem item={item} onClick={handleItemClick} shouldOpenInNewTab={shouldOpenInNewTab} />
            </Fragment>
          ))}
        </CardCollection>
      )}
      {hasNextPage &&
        // Render skeleton instead of spinner if the feed has no items yet to reserve the space
        !isScrollingDisabled &&
        (items.length ? <Spinner /> : <FeedItemListSkeleton />)}
      {isScrollingDisabled && (
        <div className={Classes.loadmore}>
          <Button.Secondary onClick={() => onLoadMore(0)} disabled={isLoading}>
            {t('l1.feeds.load_more')}
          </Button.Secondary>
        </div>
      )}
    </div>
  );
};

export default FeedItemList;
